/**
 * CivicPulse — API client. Thin wrapper around the /api/civicpulse/v1
 * namespace. All endpoints are additive: never mutate the caller's
 * data, always return the parsed JSON body.
 */
const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/civicpulse/v1`;

async function _json(url, opts = {}) {
  const res = await fetch(url, { credentials: "include", ...opts });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { msg = (await res.json()).detail || msg; } catch { /* noop */ }
    throw new Error(msg);
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

export const civic = {
  categories:   () => _json(`${BASE}/categories`),
  counters:     (geo_path) => _json(`${BASE}/stats/counters${geo_path ? `?geo_path=${encodeURIComponent(geo_path)}` : ""}`),
  recentResolved: (limit = 6) => _json(`${BASE}/stats/recent-resolved?limit=${limit}`),
  listIssues:   (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return _json(`${BASE}/issues${qs ? `?${qs}` : ""}`);
  },
  getIssue:     (pid) => _json(`${BASE}/issues/${encodeURIComponent(pid)}`),
  createIssue:  (body) => _json(`${BASE}/issues`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }),
  updateStatus: (pid, body) => _json(`${BASE}/issues/${encodeURIComponent(pid)}/status`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }),
  markAffected: (pid) => _json(`${BASE}/issues/${encodeURIComponent(pid)}/affected`, { method: "POST" }),
  toggleFollow: (pid) => _json(`${BASE}/issues/${encodeURIComponent(pid)}/follow`, { method: "POST" }),
  addComment:   (pid, body) => _json(`${BASE}/issues/${encodeURIComponent(pid)}/comment`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }),
  duplicatesCheck: (category, lat, lng) => _json(`${BASE}/duplicates/check`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ category, lat, lng }),
  }),
  share:        (pid) => _json(`${BASE}/issues/${encodeURIComponent(pid)}/share`),
  locationSummary: (path) => _json(`${BASE}/locations/${path.replace(/^\/+/, "")}/summary`),
  aiSuggest:    (photoUrl) => _json(`${BASE}/ai/suggest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ photo_url: photoUrl }),
  }),
  uploadPhoto:  async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${BASE}/upload`, { method: "POST", body: fd, credentials: "include" });
    if (!res.ok) throw new Error(`Upload failed: HTTP ${res.status}`);
    return res.json();
  },
};

// Resolve a photo URL that may be a relative /api/... path.
export function photoUrl(u) {
  if (!u) return "";
  if (/^https?:/.test(u)) return u;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
}
